import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export const TIKTOK_URL = "https://www.tiktok.com/@ultimatefootballbreaks";
const HANDLE = "@ultimatefootballbreaks";

export function TikTokIcon({ size = 18, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.1v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12v-3.2a5.78 5.78 0 0 0-.78-.05A5.79 5.79 0 1 0 15.54 18V9.4a7.38 7.38 0 0 0 4.3 1.38V7.6a4.28 4.28 0 0 1-3.24-1.78Z" />
    </svg>
  );
}

export default function TikTokSection() {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get("/tiktok").then((r) => setVideos(r.data || [])).catch(() => {});
  }, []);

  const tiles = [...videos].slice(0, 4);
  const placeholders = Math.max(0, (tiles.length ? 0 : 3));

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="tiktok-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold flex items-center gap-2">
            <TikTokIcon size={14} /> On TikTok
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-2">Watch the breaks live</h2>
          <p className="text-white/60 text-sm mt-2 max-w-xl">Come get involved in our breaks, premium boxes, big hits and great banter — over on {HANDLE}.</p>
        </div>
        <a
          href={TIKTOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ufr-primary px-6 py-3 text-sm inline-flex items-center gap-2 self-start sm:self-auto"
          data-testid="tiktok-follow-btn"
        >
          <TikTokIcon size={16} /> Follow on TikTok
        </a>
      </div>

      {tiles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tiktok-grid">
          {tiles.map((v) => (
            <div key={v.id} className="rounded-md overflow-hidden border border-white/10 bg-black" data-testid={`tiktok-tile-${v.id}`}>
              <iframe
                src={`https://www.tiktok.com/player/v1/${v.video_id}?loop=1&rel=0&autoplay=0&music_info=0&description=0`}
                title={v.caption || "TikTok video"}
                className="w-full"
                style={{ height: 560, border: "none" }}
                allow="autoplay; encrypted-media; fullscreen"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="tiktok-grid">
          {Array.from({ length: placeholders || 3 }).map((_, i) => (
            <a
              key={`ph-${i}`}
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative aspect-[9/16] overflow-hidden rounded-md border border-white/10 flex flex-col items-center justify-center gap-3"
              style={{ background: "linear-gradient(160deg, rgba(34,255,102,0.10), rgba(0,0,0,0.7))" }}
              data-testid={`tiktok-placeholder-${i}`}
            >
              <TikTokIcon size={34} className="text-white/40 group-hover:text-[color:var(--ufr-green)] transition-colors" />
              <span className="text-[11px] uppercase tracking-[0.18em] text-white/40 group-hover:text-white/70 transition-colors">Watch on TikTok</span>
              <ArrowUpRight size={16} className="absolute top-3 right-3 text-white/20 group-hover:text-white/60 transition-colors" />
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
